import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { useScopeOfWorkTemplates } from '@/hooks/settings/useScopeOfWorkTemplates';
import { stripHtml } from '@/lib/strip-html';

interface ScopeOfWorkTemplatePickerProps {
  /** The editor's current HTML — decides replace-silently vs ask-first. */
  value: string;
  onInsert: (content: string) => void;
  disabled?: boolean;
}

/**
 * Insert a Scope of Work template into the editor below (NOT-527). This is an
 * action, not a binding like TermsSelector: the document copies the template's
 * content and keeps its own wording from there — editing the template later
 * never rewrites the document. Renders nothing until the tenant has templates
 * (managed under Settings → Scope of Work), so forms stay clean by default.
 */
export function ScopeOfWorkTemplatePicker({
  value,
  onInsert,
  disabled = false,
}: ScopeOfWorkTemplatePickerProps) {
  const { data: templates = [] } = useScopeOfWorkTemplates();

  if (templates.length === 0) return null;

  function handleSelect(id: string) {
    const picked = templates.find((t) => t.id === id);
    if (!picked) return;
    // Same rule as TermsSelector: picking a template replaces what's on
    // screen — but scope of work is often half-written, so ask before
    // discarding non-empty text.
    if (
      stripHtml(value).trim() &&
      !window.confirm(`Replace the current scope of work with "${picked.name}"?`)
    ) {
      return;
    }
    onInsert(picked.content);
  }

  return (
    // Controlled to '' permanently: the trigger always reads as the action
    // ("Insert template..."), never as a held selection — the inserted text
    // lives in the editor, and re-picking the same entry must fire again.
    <Select value="" onValueChange={handleSelect} disabled={disabled}>
      <SelectTrigger
        aria-label="Insert scope of work template"
        className="h-7 w-auto max-w-56 gap-1 text-xs text-muted-foreground"
      >
        <SelectValue placeholder="Insert template..." />
      </SelectTrigger>
      <SelectContent>
        {templates.map((template) => (
          <SelectItem key={template.id} value={template.id}>
            {template.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
